import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import config from '../config';
import { Alert, ProgressBar } from 'react-bootstrap';
import HeaderNavbar from '../components/HeaderNavbar';
import Footer from '../components/Footer';
import Spinner from 'react-bootstrap/Spinner';
import api from '../lib/request';
import './Login.css';
import { evaluatePasswordStrength } from '../lib/passwordRules';

const ForgotPasswordReset = () => {
  const { hash } = useParams();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(null); // null = unknown, true/false
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordStrength = useMemo(() => evaluatePasswordStrength(password), [password]);
  const requirementStatuses = passwordStrength.statuses;
  const strengthPercent = passwordStrength.percent;
  const isPasswordStrong = passwordStrength.isStrong;
  const strengthVariant = passwordStrength.variant;
  const strengthLabel = passwordStrength.label;

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      try {
        const res = await api.get(`/api/v1/auth/password-recovery/${hash}`);
        if (!mounted) return;
        if (res && typeof res.valid !== 'undefined') {
          setValid(!!res.valid);
        } else {
          // If backend returns unexpected payload, treat as invalid
          setValid(false);
        }
      } catch (err) {
        if (!mounted) return;
        setValid(false);
        setError(err && err.message ? err.message : 'Unable to validate recovery link.');
      } finally {
        if (mounted) setChecking(false);
      }
    };
    check();
    return () => { mounted = false };
  }, [hash]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!password) return setError('Please enter a new password.');
    if (!isPasswordStrong) {
      setError('Password must meet all strength requirements.');
      return;
    }
    if (password !== confirm) return setError('Passwords do not match.');
    setLoading(true);
    try {
      await api.post(`/api/v1/auth/password-recovery/${hash}`, { password });
      setLoading(false);
      setMessage('Your password was updated. You can now Log in with your new password.');
      setPassword('');
      setConfirm('');
      setSuccess(true);
    } catch (err) {
      setLoading(false);
      const reasons = Array.isArray(err?.reasons) && err.reasons.length ? ` ${err.reasons.join(' ')}` : '';
      setError(err && err.message ? `${err.message}${reasons}` : 'An error occurred while updating the password.');
    }
  };

  if (checking) {
    return (
      <>
        <HeaderNavbar />
        <div className="row">
          <div className="Auth-form-container">
            <div className="Auth-form auth-account-form">
              <div className="Auth-form-content" style={{textAlign: 'center'}}>
                <Spinner animation="border" />
                <div className="mt-2">Checking recovery link...</div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  if (valid === false) {
    return (
      <>
        <HeaderNavbar />
        <div className="row">
          <div className="Auth-form-container">
            <div className="Auth-form auth-account-form">
              <div className="Auth-form-content" style={{textAlign: 'center'}}>
                <h3 className="Auth-form-title">Invalid or expired link</h3>
                <p className="mt-2">The recovery link is invalid or has expired. You can request a new password reset from the login page.</p>
                {error && <Alert variant="danger">{error}</Alert>}
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  if (valid === true) {
    if (success) {
      return (
        <>
          <HeaderNavbar />
          <div className="row">
            <div className="Auth-form-container">
              <div className="Auth-form auth-account-form">
                <div className="Auth-form-content" style={{textAlign: 'center'}}>
                  {message && <Alert variant="success">{message}</Alert>}
                  <div className="text-center mt-2">
                    <Link to={`${config.prefixPath}/login`} className="btn btn-link">Log in</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </>
      )
    }

    return (
      <>
        <HeaderNavbar />
        <div className="row">
          <div className="Auth-form-container">
            <form className="Auth-form auth-account-form" onSubmit={handleSubmit}>
              <div className="Auth-form-content">
                <div style={{display: 'flex', justifyContent: 'center', marginBottom: 10}}>
                  <img src="/logo.svg" alt="Logo" width="100" height="100"/>
                </div>
                <h3 className="Auth-form-title">Set a new password</h3>
                <p className="text-center mt-2">Enter a new password for your account.</p>

                <div className="form-group mt-3">
                  <label>New password</label>
                  <input
                    type="password"
                    className="form-control mt-1"
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                  <div className="mt-2">
                    <ProgressBar now={strengthPercent} variant={strengthVariant} />
                    <small className="d-block mt-1">{strengthLabel}</small>
                    <ul className="password-requirements mt-2 mb-0" style={{ listStyle: 'none', paddingLeft: 0 }}>
                      {requirementStatuses.map((req) => (
                        <li key={req.label} className={req.met ? 'text-success' : 'text-muted'}>
                          {req.met ? '[x]' : '[ ]'} {req.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="form-group mt-3">
                  <label>Confirm password</label>
                  <input
                    type="password"
                    className="form-control mt-1"
                    placeholder="Confirm new password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                </div>

                {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

                {message && <Alert variant="success" className="mt-3">{message}</Alert>}
                <div className="d-grid gap-2 mt-3">
                  {loading ? <Spinner animation="border" /> : <button type="submit" className="btn btn-primary">Update password</button>}
                </div>

              </div>
            </form>
          </div>
        </div>
        <Footer />
      </>
    )
  }

  // fallback
  return <Navigate to="/" />;
}

export default ForgotPasswordReset;
